/**
 * ProfilePage - Teacher account settings and profile management
 */

import { useState, type FormEvent } from 'react';
import { updatePassword } from 'aws-amplify/auth';

interface ProfilePageProps {
  user?: any;
}

export function ProfilePage({ user }: ProfilePageProps) {
  // Account settings state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handlePasswordChange = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setErrorMessage('Please fill in all password fields.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage('New password and confirmation do not match.');
      return;
    }

    setIsSaving(true);
    try {
      await updatePassword({ oldPassword: currentPassword, newPassword });
      setSuccessMessage('Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      const message = error?.message || 'Unable to update password. Please try again.';
      setErrorMessage(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-transparent">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#12356B]">Account Settings</h1>
          <p className="text-slate-600 mt-2">Manage your profile and account preferences</p>
        </div>

        {/* Account Information */}
        <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm p-6 space-y-4 mb-6">
          <h2 className="text-lg font-semibold text-[#12356B] mb-4">Account Information</h2>
          <div className="flex justify-between items-center py-3 border-b border-[#E2E5EC]">
            <strong className="text-sm font-medium text-slate-900">Email:</strong>
            <span className="text-sm text-slate-700">{user?.signInDetails?.loginId || 'Not available'}</span>
          </div>
          <div className="flex justify-between items-center py-3 border-b border-[#E2E5EC]">
            <strong className="text-sm font-medium text-slate-900">User ID:</strong>
            <span className="text-sm text-slate-700">{user?.userId || 'Not available'}</span>
          </div>
        </div>

        {/* Change Password */}
        <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-[#12356B] mb-4">Change Password</h2>
          <form className="space-y-4" onSubmit={handlePasswordChange}>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="current-password">
                Current password
              </label>
              <input
                id="current-password"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                className="w-full rounded-lg border border-[#CED4DE] px-3 py-2 text-sm text-slate-900 focus:border-[#12356B] focus:outline-none focus:ring-2 focus:ring-[#12356B]/25"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="new-password">
                New password
              </label>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                className="w-full rounded-lg border border-[#CED4DE] px-3 py-2 text-sm text-slate-900 focus:border-[#12356B] focus:outline-none focus:ring-2 focus:ring-[#12356B]/25"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="confirm-password">
                Confirm new password
              </label>
              <input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="w-full rounded-lg border border-[#CED4DE] px-3 py-2 text-sm text-slate-900 focus:border-[#12356B] focus:outline-none focus:ring-2 focus:ring-[#12356B]/25"
              />
            </div>
            {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
            {successMessage && <p className="text-sm text-[#367A25]">{successMessage}</p>}
            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex items-center justify-center rounded-lg bg-[#367A25] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#2d6820] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSaving ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
